import Popover from '@components/Common/Popover.js';
import toast from '@components/Common/Toast.js';
import Tooltip from '@components/Common/Tooltip.js';
import YesNoPromptModal from '@components/Modals/YesNoPromptModal.js';
import SelectOptionsModal from '@components/Projects/Modals/SelectOptionsModal.js';
import { IS_MOBILE } from '@constants/global.js';
import {
  ArrowLeftIcon,
  AscendingIcon,
  BurgerMenuIcon,
  DescendingIcon,
  DotsVerticalIcon,
  EditIcon,
  EyeClosedIcon,
  PlusIcon,
  ScaleIcon,
  TrashIcon,
  UIIcon,
  XIcon,
} from '@icons/index.js';
import { COLUMN_EVENT_TYPES, columnUpdate } from '@signals/project/index.js';
import ProjectsState from '@states/Projects';
import { UIButton, UIDiv, UIInput, UIText } from '@ui';
import {
  addColumn,
  defaultColumns,
  deleteColumn,
  getFolderColumns,
  getVisibleColumns,
  orderColumn,
  renameColumn,
  resizeColumn,
  setColumnVisibility,
  sortColumn,
} from '@utils/columnUtils';

export default class ProjectListColumns extends UIDiv {
  constructor(currentFolderId, canCreate, canUpdate, isAnonymousView) {
    super();
    this.currentFolderId = currentFolderId;
    this.canCreate = canCreate;
    this.canUpdate = canUpdate;
    this.isAnonymousView = isAnonymousView;

    // Track editing state for drag prevention
    this.isAnyColumnEditing = false;

    this.addClass('flex w-full border-b border-gray-200');

    // Subscribe to all column events
    columnUpdate.add(data => {
      this.handleColumnChange(data);
    });

    this.render();
  }

  render() {
    // Clear existing content
    this.clear();

    // get the visible columns from the project state based on the current folder ID
    const visibleColumns = getVisibleColumns(this.currentFolderId);

    visibleColumns.forEach(column => {
      // do not show 'Assignees' column if the user is not in an organization project page
      const { rootFolderOrganizationId } = ProjectsState.projects;
      if (!rootFolderOrganizationId && column.type === 'people') {
        return;
      }
      const columnWrapper = this.createColumnWrapper(column);
      this.add(columnWrapper);
    });
    // create a column to add new columns
    if (this.canCreate) {
      const createColumnCell = new UIDiv();
      createColumnCell.setStyle('width', ['50px']);
      createColumnCell.setId('column-create-button');
      const createColumnButton = this.createColumnCreationButton();
      createColumnCell.add(createColumnButton);
      this.add(createColumnCell);
    }

    // Add a spacer column to fill remaining space
    const spacerColumn = new UIDiv();
    spacerColumn.addClass('flex-1');
    spacerColumn.setStyle('min-width', ['0']);
    spacerColumn.setStyle('max-width', ['none']);
    this.add(spacerColumn);
  }

  createColumnWrapper(column) {
    const wrapper = new UIDiv().setId(`column-${column.key}-${column.id}`);
    wrapper.addClass(
      'group p-2 flex-shrink-0 hover:bg-gray-50 cursor-pointer relative',
    );
    if (!this.isAnonymousView) wrapper.addClass('border-r border-gray-200');
    const width = this.isAnonymousView ? '250px' : column.width || '150px';
    wrapper.setStyle('width', [width]);

    // Add drag functionality only if column is not fixed
    if (!column.isFixed && this.canUpdate) {
      this.addDragFunctionality(wrapper, column);
    }

    this.createColumnContent(column, wrapper);

    return wrapper;
  }

  createColumnContent(column, wrapper) {
    // Clear existing content in the wrapper
    wrapper.clear();

    // Remove any existing mobile click handlers to prevent multiple handlers
    if (wrapper._mobileClickHandler) {
      wrapper.dom.removeEventListener('click', wrapper._mobileClickHandler);
      wrapper._mobileClickHandler = null;
    }

    // Column header with title and key
    const header = new UIDiv();
    header.addClass('flex items-center justify-center');

    const title = this.createColumnTitle(column, wrapper);
    header.add(title);

    const menu = this.createColumnMenu(column, wrapper);
    // Only add menu to header on desktop (on mobile it uses wrapper as trigger)
    if (menu && !IS_MOBILE() && this.canUpdate) {
      header.add(menu);
    }

    wrapper.add(header);

    // Add resizer handle (not on mobile)
    if (!IS_MOBILE() && this.canUpdate) {
      this.addResizerHandle(column, wrapper);
    }
  }

  createColumnTitle(column, wrapper) {
    const titleContainer = new UIDiv();
    titleContainer.addClass('rounded-md flex-1 min-w-0 mr-2 flex items-center'); // Added flex items-center

    const title = new UIText(column.title);
    title.addClass('font-medium truncate block');

    titleContainer.add(title);

    if (!IS_MOBILE()) {
      title.onMouseEnter(() => {
        Tooltip.show(title, column.title, 'top');
      });
      title.onMouseLeave(() => {
        Tooltip.hide();
      });
    }

    // Add sort indicator if this column is sorted
    if (column.sortingBy) {
      const sortIndicator = new UIText(
        column.sortingBy === 'asc' ? ' ↑' : ' ↓',
      );
      sortIndicator.addClass('text-sm text-gray-600 ml-1 flex-shrink-0');
      titleContainer.add(sortIndicator);
    }

    // Only allow editing on desktop for custom columns
    if (column.isCustom && !IS_MOBILE()) {
      titleContainer.addClass(
        'cursor-text border border-transparent hover:border-gray-300 transition-all duration-200',
      );
      // Add click event to enter edit mode
      titleContainer.onClick(() => {
        this.enterEditMode(column, wrapper);
      });
    }

    return titleContainer;
  }

  createColumnCreationButton() {
    const createColumnButton = new UIButton();
    const copyButtonIcon = new PlusIcon({
      size: 16,
      color: 'none',
    });
    createColumnButton.addClass(
      '!filter-none flex items-center justify-center h-full w-full hover:bg-gray-100',
    );
    createColumnButton.add(copyButtonIcon);
    createColumnButton.onMouseEnter(() => {
      Tooltip.show(
        createColumnButton,
        i18next.t('pages.projects.columns.createNew'),
        'top',
      );
    });
    createColumnButton.onMouseLeave(() => {
      Tooltip.hide();
    });

    const popoverContent = new UIDiv().setClass('p-4 w-64');

    const fieldsSection = new UIDiv().setClass('mb-1');
    const fieldsTitle = new UIDiv().setClass('font-semibold mb-2');
    fieldsTitle.setTextContent(i18next.t('pages.projects.columns.add'));
    fieldsSection.add(fieldsTitle);

    defaultColumns.forEach(column => {
      // Do not display 'people' column if the user is not in an organization project page
      const { rootFolderOrganizationId } = ProjectsState.projects;
      if (!rootFolderOrganizationId && column.key === 'people') {
        return;
      }
      const item = new UIDiv().setClass(
        'flex items-center gap-3 p-1.5 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors',
      );

      const columnIcon = new UIIcon(column.icon, {
        size: 16,
        color: 'none',
        strokeColor: 'white',
        className: `${column.bgColor} !w-6 !h-6 rounded-md flex items-center justify-center p-0.5`,
      });

      // Column name
      const columnName = new UIText(column.name);
      columnName.setClass('flex-1 font-medium truncate');

      item.add(columnIcon);
      item.add(columnName);

      item.onClick(async () => {
        const visibleColumns = getVisibleColumns(this.currentFolderId);
        // Check if the column name is already use then add suffix
        let columnSuffix = 1;
        let newColumnName = column.defaultTitle;

        // Keep incrementing suffix until the name is unique
        while (visibleColumns.some(col => col.title === newColumnName)) {
          newColumnName = `${column.defaultTitle} ${columnSuffix}`;
          columnSuffix++;
        }

        try {
          const columnData = {
            key:
              newColumnName.toLowerCase().replace(/\s+/g, '_') +
              '_' +
              Math.floor(Math.random() * 1000),
            title: newColumnName,
            type: column.key,
          };

          if (column.defaultOptions) {
            columnData.options = column.defaultOptions;
          }

          // Add the new column to the current folder / refresh the columns or add it to the state
          await addColumn(columnData, this.currentFolderId);
        } catch (error) {
          console.error('Failed to add column:', error);
          toast.error(`Failed to add column: ${error.message}`);
        }
      });

      fieldsSection.add(item);
    });

    popoverContent.add(fieldsSection);

    createColumnButton.onClick(e => {
      e.stopPropagation();
      Popover.show({
        trigger: createColumnButton,
        content: popoverContent,
        placement: 'left-start',
        hideOnSelect: true,
        hideOnScroll: true,
      });
    });

    return createColumnButton;
  }

  createMenuOption(
    IconClass,
    text,
    onClick,
    iconClassName = '',
    isDisabled = false,
  ) {
    const option = new UIButton();
    option.addClass(
      'w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 hover:!filter-none',
    );

    const icon = new IconClass({
      size: 18,
      color: 'none',
      strokeColor: 'black',
      className: iconClassName,
    });
    option.add(icon);
    option.add(new UIText(text));
    if (isDisabled) {
      option.addClass('opacity-50 cursor-not-allowed');
      option.setDisabled(true);
      option.onClick(e => e.stopPropagation());
    } else {
      option.onClick(onClick);
    }

    return option;
  }

  createColumnMenu(column, wrapper) {
    // Menu content
    const menuContent = new UIDiv();
    menuContent.addClass('py-1 min-w-32');

    // Hide option - available for all columns except isFixed=true
    if (!column.isFixed) {
      const hideOption = this.createMenuOption(
        EyeClosedIcon,
        i18next.t('pages.projects.columns.hide'),
        async () => {
          try {
            if (column.id) {
              await setColumnVisibility(
                this.currentFolderId,
                column.id,
                false,
                this.canUpdate,
              );
            }
          } catch (error) {
            console.error('Failed to hide column:', error);
            toast.error(`Failed to hide column: ${error.message}`);
          }
        },
      );
      menuContent.add(hideOption);
    }

    menuContent.add(new UIDiv().setClass('h-[1.5px] w-full bg-gray-100'));

    // Sort Ascending option
    const sortAscOption = this.createMenuOption(
      AscendingIcon,
      i18next.t('pages.projects.columns.sortAsc'),
      async () => {
        try {
          await sortColumn(this.currentFolderId, column.id, 'asc');
          this.render();
        } catch (error) {
          console.error('Failed to sort column:', error);
          toast.error(`Failed to sort column: ${error.message}`);
        }
      },
    );
    menuContent.add(sortAscOption);

    // Sort Descending option
    const sortDescOption = this.createMenuOption(
      DescendingIcon,
      i18next.t('pages.projects.columns.sortDesc'),
      async () => {
        try {
          await sortColumn(this.currentFolderId, column.id, 'desc');
          this.render();
        } catch (error) {
          console.error('Failed to sort column:', error);
          toast.error(`Failed to sort column: ${error.message}`);
        }
      },
    );
    menuContent.add(sortDescOption);
    menuContent.add(new UIDiv().setClass('h-[1.5px] w-full bg-gray-100'));

    // Manage Options - only for select columns
    if (column.type === 'select' && column.id) {
      const manageOptionsOption = this.createMenuOption(
        BurgerMenuIcon,
        i18next.t('pages.projects.columns.manageOptions'),
        () => {
          this.showSelectOptionsModal(column);
        },
      );
      menuContent.add(manageOptionsOption);
    }

    // Delete - Edit option - only for isCustom=true
    if (column.isCustom) {
      const editOption = this.createMenuOption(
        EditIcon,
        i18next.t('common.edit'),
        () => {
          this.enterEditMode(column, wrapper);
        },
      );
      menuContent.add(editOption);
      const deleteOption = this.createMenuOption(
        TrashIcon,
        i18next.t('common.delete'),
        async () => {
          const modal = new YesNoPromptModal(
            i18next.t('pages.projects.columns.deleteTitle'),
            i18next.t('pages.projects.columns.deleteConfirm', {
              title: column.title,
            }),
            {
              confirmText: i18next.t('common.delete'),
              cancelText: i18next.t('common.cancel'),
              confirmClass:
                '!bg-[var(--o-hive-error-100)] hover:!bg-[var(--o-hive-error-80)] text-white',
            },
          );

          const result = await modal.show();
          if (result) {
            try {
              if (column.id)
                await deleteColumn(this.currentFolderId, column.id);
              this.render();
            } catch (error) {
              console.error('Failed to delete column:', error);
              toast.error(`Failed to delete column: ${error.message}`);
            }
          }
        },
      );
      menuContent.add(deleteOption);
    }

    // if is mobile add resize as option to menu
    if (IS_MOBILE() && this.canUpdate) {
      const resizeOption = this.createMenuOption(
        ScaleIcon,
        i18next.t('pages.projects.columns.resize'),
        () => {
          this.addResizerHandle(column, wrapper, true);
        },
      );
      menuContent.add(resizeOption);
    }

    if (IS_MOBILE() && !column.isFixed) {
      const allColumns = getFolderColumns(this.currentFolderId);
      const sortedColumns = [...allColumns].sort(
        (a, b) => (a.order || 0) - (b.order || 0),
      );
      const currentIndex = sortedColumns.findIndex(col => col.id === column.id);
      const leftColumn = sortedColumns[currentIndex - 1];

      const cantMoveRight = currentIndex >= sortedColumns.length - 1;
      const cantMoveLeft = currentIndex <= 0 || leftColumn.isFixed;

      menuContent.add(new UIDiv().setClass('h-[1.5px] w-full bg-gray-100'));
      const moveLeftOption = this.createMenuOption(
        ArrowLeftIcon,
        i18next.t('pages.projects.columns.moveLeft'),
        async () => {
          await this.#moveColumn(column, -1);
        },
        '',
        cantMoveLeft,
      );
      menuContent.add(moveLeftOption);

      const moveRightOption = this.createMenuOption(
        ArrowLeftIcon,
        i18next.t('pages.projects.columns.moveRight'),
        async () => {
          await this.#moveColumn(column, 1);
        },
        'rotate-180',
        cantMoveRight,
      );
      menuContent.add(moveRightOption);
    }

    // Different behavior for mobile vs desktop
    if (IS_MOBILE()) {
      // Create the click handler function
      const mobileClickHandler = e => {
        e.stopPropagation();
        Popover.show({
          trigger: wrapper,
          content: menuContent,
          placement: 'bottom-start',
          hideOnSelect: true,
          hideOnScroll: true,
        });
      };

      // Store reference to the handler for cleanup
      wrapper._mobileClickHandler = mobileClickHandler;

      // Add the click handler
      wrapper.dom.addEventListener('click', mobileClickHandler);

      return null;
    } else {
      // On desktop: Use the 3-dots menu trigger
      const menuTrigger = new UIDiv();
      menuTrigger.addClass(
        'rotate-90 mr-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-gray-200 z-10 rounded-md',
      );

      const dotsIcon = new DotsVerticalIcon({
        size: 18,
        color: 'none',
      });
      menuTrigger.add(dotsIcon);
      menuTrigger.addClass('w-6 h-6');

      menuTrigger.onClick(e => {
        e.stopPropagation();
        Popover.show({
          trigger: menuTrigger,
          content: menuContent,
          placement: 'bottom-end',
          hideOnScroll: true,
          hideOnSelect: true,
        });
      });

      return menuTrigger;
    }
  }

  /**
   * Set the editing state for columns
   * Called when entering/exiting edit mode
   */
  setColumnEditingState(isEditing) {
    this.isAnyColumnEditing = isEditing;
  }

  enterEditMode(column, wrapper) {
    // Notify that a column is entering edit mode
    this.setColumnEditingState(true);

    // Clear the wrapper content
    wrapper.clear();

    // Create input that fills the wrapper
    const input = new UIInput(column.title);
    input.addClass(
      'w-full h-full cursor-text border border-[var(--o-hive-primary-80)] rounded-md px-2 py-1 text-center font-medium focus:outline-none focus:!ring-2 focus:!ring-[var(--o-hive-primary-80)]',
    );

    // Add the input to the wrapper
    wrapper.add(input);
    // Focus the input and select all text
    setTimeout(() => {
      input.dom.focus();
      input.dom.select();
    }, 0);

    // Prevent edit mode from exiting when clicking inside the input
    input.onClick(e => {
      e.stopPropagation();
    });

    let isEditingSaved = false; // Flag to prevent multiple save calls

    // Handle save on Enter or blur
    const saveEdit = async () => {
      if (isEditingSaved) return; // Prevent multiple executions
      isEditingSaved = true;
      const newTitle = input.getValue().trim();

      const visibleColumns = getVisibleColumns(this.currentFolderId);

      while (visibleColumns.some(col => col.title === newTitle)) {
        toast.error(i18next.t('pages.projects.toast.column_exists'));
        this.setColumnEditingState(false);
        this.createColumnContent(column, wrapper);
        return;
      }

      if (newTitle && newTitle !== column.title) {
        try {
          await renameColumn(this.currentFolderId, column.id, newTitle);
        } catch (error) {
          console.error('Failed to rename column:', error);
          toast.error(`Failed to rename column: ${error.message}`);
        }
      }
      // Notify that editing is finished
      this.setColumnEditingState(false);
      this.createColumnContent(column, wrapper);
    };

    // Handle cancel on Escape
    const cancelEdit = () => {
      if (isEditingSaved) return; // Prevent multiple executions
      isEditingSaved = true;

      // Notify that editing is finished
      this.setColumnEditingState(false);
      this.createColumnContent(column, wrapper);
    };

    // Add event listeners
    input.onKeyDown(event => {
      if (event.key === 'Enter') {
        event.preventDefault();
        saveEdit();
      } else if (event.key === 'Escape') {
        cancelEdit();
      }
    });

    input.onBlur(() => {
      saveEdit();
    });
  }

  addResizerHandle(column, wrapper, isMobile = false) {
    const originalWidth = column.width;
    // Create resizer handle
    const resizer = new UIDiv();
    resizer.addClass(
      isMobile
        ? 'absolute top-0 -right-0.5 w-3.5 rounded-md h-full bg-[var(--o-hive-primary-80)] cursor-col-resize z-10'
        : 'absolute top-0 -right-0.5 w-1.5 rounded-md h-full bg-gray-200 hover:bg-[var(--o-hive-primary-80)] cursor-col-resize opacity-0 group-hover:opacity-100 transition-opacity duration-200',
    );

    // Resizing functionality
    let isResizing = false;
    let startX = 0;
    let startWidth = 0;

    const startResize = e => {
      e.preventDefault();
      e.stopPropagation();

      isResizing = true;
      startX = e.clientX;
      startWidth = parseInt(column.width);

      if (IS_MOBILE()) return; // Skip if mobile
      resizer.addClass('is-resizing bg-[var(--o-hive-primary-80)]');

      // Add global mouse events
      document.addEventListener('mousemove', handleResize);
      document.addEventListener('mouseup', stopResize);
    };

    const handleResize = e => {
      if (!isResizing) return;

      e.preventDefault();
      const deltaX = e.clientX - startX;
      const newWidth = Math.max(100, startWidth + deltaX); // Minimum width of 100px, I think it is good enough

      // Update column width visually
      wrapper.setStyle('width', [`${newWidth}px`]);

      // Update the column object
      column.width = `${newWidth}px`;

      // Trigger real-time width changing event for rows to update immediately
      columnUpdate.dispatch({
        type: COLUMN_EVENT_TYPES.WIDTH_CHANGING,
        columnId: column.id,
        newWidth: `${newWidth}px`,
        currentFolderId: this.currentFolderId,
        column: { ...column },
      });
    };

    const stopResize = async e => {
      if (!isResizing) return;

      isResizing = false;
      resizer.removeClass('is-resizing bg-[var(--o-hive-primary-80)]');

      // Remove global mouse events
      document.removeEventListener('mousemove', handleResize);
      document.removeEventListener('mouseup', stopResize);

      if (IS_MOBILE()) {
        // Cleanup touch events if on mobile
        cleanupEvents();
      }

      const newWidth = parseInt(column.width);
      await resizeColumn(this.currentFolderId, column.id, newWidth);
    };

    // Mouse events for resizer
    resizer.onMouseDown(startResize);

    // Touch events for mobile
    resizer.onTouchStart(e => {
      const touch = e.touches[0];
      startResize({
        preventDefault: () => e.preventDefault(),
        stopPropagation: () => e.stopPropagation(),
        clientX: touch.clientX,
      });
    });

    const handleTouchMove = e => {
      if (!isResizing) return;
      const touch = e.touches[0];
      handleResize({
        preventDefault: () => e.preventDefault(),
        clientX: touch.clientX,
      });
    };

    const handleTouchEnd = e => {
      if (!isResizing) return;
      stopResize({});
    };

    // Cleanup function to remove touch events
    const cleanupEvents = () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };

    if (isMobile) {
      document.addEventListener('touchmove', handleTouchMove, {
        passive: false,
      });
      document.addEventListener('touchend', handleTouchEnd);
      // Create a container for the confirm/cancel icons positioned below the resizer
      const controls = new UIDiv();
      controls.addClass(
        'absolute bottom-[-40px] right-0 flex items-center gap-2 p-1 rounded-md bg-white border shadow-lg z-30',
      );

      const cancelButton = new UIButton();
      cancelButton.addClass('hover:bg-gray-100 p-1 rounded-md');
      cancelButton.add(
        new XIcon({ size: 20, color: 'none', strokeColor: 'red' }),
      );
      controls.add(cancelButton);

      cancelButton.onClick(e => {
        e.stopPropagation();
        cleanupEvents();
        column.width = originalWidth; // Revert to original width
        wrapper.setStyle('width', [originalWidth]);
        // Recreate normal column content
        this.createColumnContent(column, wrapper);
      });

      wrapper.add(controls);
    }

    wrapper.add(resizer);
  }

  showSelectOptionsModal(column) {
    const modal = new SelectOptionsModal(column, this.currentFolderId, () => {
      this.render();
    });
    modal.show();
  }

  /**
   * Handle column events from external sources
   * @param {Object} data - The column event data
   */
  handleColumnChange(data) {
    // Only respond to changes from the same folder
    if (data.currentFolderId === this.currentFolderId) {
      switch (data.type) {
        case COLUMN_EVENT_TYPES.VISIBILITY_CHANGED:
        case COLUMN_EVENT_TYPES.TITLE_CHANGED:
        case COLUMN_EVENT_TYPES.WIDTH_CHANGED:
        case COLUMN_EVENT_TYPES.DELETED:
        case COLUMN_EVENT_TYPES.SORTED:
        case COLUMN_EVENT_TYPES.RESET:
        case COLUMN_EVENT_TYPES.ORDERING:
          this.render();
          break;
        case COLUMN_EVENT_TYPES.WIDTH_CHANGING:
          break;

        case COLUMN_EVENT_TYPES.ADDED:
          // Handle column addition with incremental update
          this.appendNewColumn(data);
          break;

        default:
          break;
      }
    }
  }

  /**
   * Append new columns to the existing layout without full re-render
   * @param {Array} data - the new column to append
   */
  appendNewColumn(data) {
    if (!data || !data.column) return;
    const column = data.column;

    // Checks if the returned array of elements has any items.
    const newColumnDomId = `column-${column.key}-${column.id}`;
    if (this.find(`#${newColumnDomId}`).length > 0) {
      return;
    }

    const columnWrapper = this.createColumnWrapper(column);

    // Get all direct child elements to find the insertion index.
    const children = this.getChildren();
    const index = children.findIndex(
      child => child.id === 'column-create-button',
    );

    if (index !== -1) {
      // Insert before the add column button
      this.insertAt(columnWrapper, index);
    } else if (children.length > 0) {
      // Fallback: insert before spacer if no add button found
      this.insertAt(columnWrapper, children.length - 1);
    } else {
      // No children exist, just add the new column.
      this.add(columnWrapper);
    }
  }

  /** COLUMNS DRAG AND DROP FUNCTIONALITY */
  addDragFunctionality(wrapper, column) {
    if (IS_MOBILE()) return; // Skip drag functionality on mobile
    // Make the wrapper draggable
    wrapper.dom.draggable = true;
    wrapper.addClass('draggable-column');

    // Add visual feedback for draggable state
    wrapper.onMouseEnter(() => {
      if (!column.isFixed && !this.isAnyColumnEditing) {
        wrapper.setStyle('cursor', ['grab']);
      }
    });

    // Drag start event
    wrapper.onDragStart(e => {
      // Prevent dragging if any column is in edit mode
      if (this.isAnyColumnEditing) {
        e.preventDefault();
        return false;
      }

      wrapper.addClass('opacity-50');
      wrapper.setStyle('cursor', ['grabbing']);
      e.dataTransfer.setData('text/plain', column.id);
      e.dataTransfer.effectAllowed = 'move';

      // Create drag image
      const dragImage = wrapper.dom.cloneNode(true);
      dragImage.style.transform = 'rotate(2deg)';
      dragImage.style.opacity = '0.8';
      document.body.appendChild(dragImage);
      e.dataTransfer.setDragImage(dragImage, 0, 0);
      setTimeout(() => document.body.removeChild(dragImage), 0);
    });

    // Drag end event
    wrapper.onDragEnd(() => {
      wrapper.removeClass('opacity-50');
      wrapper.setStyle('cursor', ['grab']);

      // Final cleanup of all indicators
      const allIndicated = document.querySelectorAll(
        '.drop-indicator-left, .drop-indicator-right',
      );
      allIndicated.forEach(el =>
        el.classList.remove('drop-indicator-left', 'drop-indicator-right'),
      );
    });

    // Drag over event
    wrapper.onDragOver(e => {
      // Prevent drag over if any column is editing
      if (this.isAnyColumnEditing) return;

      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';

      const dragging = document.querySelector('.opacity-50');
      if (dragging && dragging !== wrapper.dom) {
        // Clear existing indicators from other columns
        const indicatedColumn = document.querySelector(
          '.drop-indicator-left, .drop-indicator-right',
        );
        if (indicatedColumn && indicatedColumn !== wrapper.dom) {
          indicatedColumn.classList.remove(
            'drop-indicator-left',
            'drop-indicator-right',
          );
        }

        const rect = wrapper.getBoundingClientRect();
        const midpoint = rect.left + rect.width / 2;

        if (e.clientX < midpoint) {
          wrapper.addClass('drop-indicator-left');
          wrapper.removeClass('drop-indicator-right');
        } else {
          wrapper.addClass('drop-indicator-right');
          wrapper.removeClass('drop-indicator-left');
        }
      }
    });

    // Drag leave event
    wrapper.onDragLeave(() => {
      wrapper.removeClass('drop-indicator-left', 'drop-indicator-right');
    });

    // Drop event
    wrapper.onDrop(async e => {
      // Prevent drop if any column is editing
      if (this.isAnyColumnEditing) return;

      e.preventDefault();
      wrapper.removeClass('drop-indicator-left', 'drop-indicator-right');

      const draggedColumnId = parseInt(
        e.dataTransfer.getData('text/plain'),
        10,
      );
      const targetColumnId = column.id;

      if (draggedColumnId !== targetColumnId) {
        await this.handleColumnReorder(draggedColumnId, targetColumnId, e);
      }
    });
  }

  async handleColumnReorder(draggedColumnId, targetColumnId, dropEvent) {
    try {
      // Get current column order
      const allColumns = getFolderColumns(this.currentFolderId);

      // Find the dragged and target columns
      const draggedColumn = allColumns.find(col => col.id === draggedColumnId);
      const targetColumn = allColumns.find(col => col.id === targetColumnId);

      if (!draggedColumn || !targetColumn || draggedColumn.isFixed) {
        return; // Don't allow reordering fixed columns
      }

      // Determine drop position based on mouse position
      const targetWrapper = document.getElementById(
        `column-${targetColumn.key}-${targetColumn.id}`,
      );
      const rect = targetWrapper.getBoundingClientRect();
      const midpoint = rect.left + rect.width / 2;
      const dropAfter = dropEvent.clientX > midpoint;

      // Create new order array
      const newOrder = this.calculateNewColumnOrder(
        allColumns,
        draggedColumn,
        targetColumn,
        dropAfter,
      );

      await orderColumn(this.currentFolderId, newOrder);
    } catch (error) {
      console.error('Failed to reorder columns:', error);
      toast.error(`Failed to reorder columns: ${error.message}`);
    }
  }

  /**
   * Private helper method to move a column in a specified direction.
   * @param {Object} column - The column to move.
   * @param {number} direction - The direction to move (-1 for left, 1 for right).
   */
  async #moveColumn(column, direction) {
    // Determine direction for logging and boundary checks
    const isMovingLeft = direction === -1;
    const directionText = isMovingLeft ? 'left' : 'right';

    try {
      const allColumns = getFolderColumns(this.currentFolderId);
      const sortedColumns = [...allColumns].sort(
        (a, b) => (a.order || 0) - (b.order || 0),
      );

      const currentIndex = sortedColumns.findIndex(col => col.id === column.id);
      const targetIndex = currentIndex + direction;

      // --- Generalized Boundary Checks ---
      // Check if the move is out of bounds
      if (targetIndex < 0 || targetIndex >= sortedColumns.length) {
        return;
      }

      // Specific check for moving left past a fixed column
      const targetColumn = sortedColumns[targetIndex];
      if (isMovingLeft && targetColumn.isFixed) {
        return;
      }

      // Use array destructuring to swap the elements in place
      const newSortedColumns = [...sortedColumns];
      [newSortedColumns[currentIndex], newSortedColumns[targetIndex]] = [
        newSortedColumns[targetIndex],
        newSortedColumns[currentIndex],
      ];

      // Create the final payload for the API
      const newOrder = newSortedColumns.map((col, index) => ({
        id: col.id,
        order: index,
      }));

      await orderColumn(this.currentFolderId, newOrder);
    } catch (error) {
      console.error(`Failed to move column ${directionText}:`, error);
      toast.error(`Failed to move column ${directionText}: ${error.message}`);
    }
  }

  calculateNewColumnOrder(allColumns, draggedColumn, targetColumn, dropAfter) {
    // Create a copy of columns sorted by current order
    const sortedColumns = [...allColumns].sort(
      (a, b) => (a.order || 0) - (b.order || 0),
    );

    // Remove dragged column from current position
    const filteredColumns = sortedColumns.filter(
      col => col.id !== draggedColumn.id,
    );

    // Find target index
    const targetIndex = filteredColumns.findIndex(
      col => col.id === targetColumn.id,
    );
    const insertIndex = dropAfter ? targetIndex + 1 : targetIndex;

    // Insert dragged column at new position
    filteredColumns.splice(insertIndex, 0, draggedColumn);

    // Return new order array with id and order
    return filteredColumns.map((col, index) => ({
      id: col.id,
      order: index,
    }));
  }

  /**
   * Cleanup method to remove event subscriptions
   * Call this when the component is destroyed
   */
  destroy() {
    // Clean up column change subscription
    columnUpdate.remove(this.handleColumnChange);

    // Clear references
    this.currentFolderId = null;
    this.canCreate = null;
    this.canUpdate = null;
    this.isAnyColumnEditing = null;
  }
}
